import { useState } from 'react';
import { observer } from 'mobx-react';
import { Link } from 'react-router-dom';
import { Button, Grid, Typography } from 'antd';
import { StarOutlined } from '@ant-design/icons';
import { SectionCard } from '@/components/SectionCard';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import type { CoverageRow, EvaluationWorklist } from '@/api/evaluationWorklist';
import { useWorkspace } from './EvaluationWorkspace';
import { coverageResult, coverageSummary, evaluationPath } from './worklistWords';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// Coverage with denominators (screen 01, frame A right column; the Coverage tab on its own). It is counted per
// scenario CATEGORY (aivalidation.ScenarioCategory) because that is what exists: reviewed requirements arrive with
// SI-05b, so this never says "coverage by requirement". A category with no library scenario stays on screen as 0,
// uncovered, with a Generate scoped to it. Drafts are not coverage.
// ─────────────────────────────────────────────────────────────────────────────

export const REQUIREMENT_NOTE =
  'Counted per scenario category, not per requirement: reviewed requirements arrive with SI-05b, so no requirement is counted as covered yet.';

export const CoverageCard = observer(function CoverageCard({ data, compact }: { data: EvaluationWorklist; compact?: boolean }) {
  const ws = useWorkspace();
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const narrow = !Grid.useBreakpoint().md;
  const [showUncovered, setShowUncovered] = useState(!compact);
  const rows = data.coverage.rows;
  const covered = rows.filter((r) => r.scenarios > 0);
  const uncovered = rows.filter((r) => r.scenarios === 0);
  const shown = showUncovered ? rows : covered;
  const line = isDark ? colors.slate[700] : colors.slate[200];
  const edited = data.notAssessed.find((g) => g.kind === 'EditedSinceRun')?.count ?? 0;

  return (
    <SectionCard
      title={
        <span>
          Coverage by category{' '}
          <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
            with denominators
          </Text>
        </span>
      }
    >
      <div data-testid="si-coverage" role="table" aria-label="Coverage by scenario category">
        {!narrow && (
          <div role="row" style={{ display: 'grid', gridTemplateColumns: columns(compact), gap: 10, padding: '0 0 6px', borderBottom: `1px solid ${line}`, fontSize: 12, color: colors.slate[500] }}>
            <span role="columnheader">Category</span>
            <span role="columnheader">Scenarios</span>
            <span role="columnheader">Result</span>
          </div>
        )}
        {shown.map((r) => (
          <Row key={r.code} row={r} isDark={isDark} narrow={narrow} compact={compact} line={line} onGenerate={() => ws.openGenerate(`Test how this agent handles ${r.label.toLowerCase()}.`)} />
        ))}
        {rows.length === 0 && <Text type="secondary">No scenario category is recorded for this agent.</Text>}
      </div>
      {compact && uncovered.length > 0 && (
        <Button type="link" size="small" style={{ paddingLeft: 0, marginTop: 6 }} onClick={() => setShowUncovered((v) => !v)}>
          {showUncovered ? 'Hide the categories with no scenario' : `Show the ${uncovered.length} categor${uncovered.length === 1 ? 'y' : 'ies'} with no scenario`}
        </Button>
      )}
      <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 10 }} data-testid="si-coverage-summary">
        {coverageSummary(data)}{' '}
        {data.latestRunId != null
          ? `Evidence freshness: the latest run here is #${data.latestRunId}${edited ? `; ${edited} scenario${edited === 1 ? ' was' : 's were'} edited since ${edited === 1 ? 'its' : 'their'} last run and count${edited === 1 ? 's' : ''} as Not assessed` : ''}. `
          : 'No run has acted in this practice yet, so every scenario is Not assessed. '}
        {/* The Coverage tab says it above the table; the Overview's card says it here. */}
        {compact && (
          <>
            {REQUIREMENT_NOTE} <Link to={evaluationPath('coverage', data.agentId)}>Open Coverage</Link>
          </>
        )}
      </Text>
    </SectionCard>
  );
});

/** The Overview's side column is narrow; the Coverage tab gives the result its room. */
const columns = (compact?: boolean) => (compact ? 'minmax(0, 1fr) 74px minmax(0, 150px)' : 'minmax(0, 1.4fr) 90px minmax(0, 1fr)');

function Row({ row, isDark, narrow, compact, line, onGenerate }: { row: CoverageRow; isDark: boolean; narrow: boolean; compact?: boolean; line: string; onGenerate: () => void }) {
  const result = coverageResult(row);
  return (
    <div
      role="row"
      data-testid={`si-coverage-${row.code}`}
      style={{
        display: 'grid',
        gridTemplateColumns: narrow ? 'minmax(0, 1fr) auto' : columns(compact),
        gap: narrow ? '4px 10px' : 10,
        padding: '10px 0',
        borderBottom: `1px solid ${line}`,
        alignItems: 'start',
      }}
    >
      <div role="cell" style={{ fontSize: 13, minWidth: 0 }}>
        {row.label}
        <Text type="secondary" style={{ fontSize: 11.5, marginLeft: 6 }}>{row.inTaxonomy ? row.code : 'not in the category list'}</Text>
      </div>
      <div role="cell" style={{ fontSize: 13, fontVariantNumeric: 'tabular-nums', textAlign: narrow ? 'right' : 'left' }}>
        {narrow && <Text type="secondary" style={{ fontSize: 11.5 }}>Scenarios </Text>}
        <b style={{ color: row.scenarios === 0 ? (isDark ? colors.red[500] : colors.red[600]) : undefined }}>{row.scenarios}</b>
      </div>
      <div role="cell" style={{ display: 'grid', gap: 4, gridColumn: narrow ? '1 / -1' : undefined }}>
        {row.scenarios === 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <Text type="secondary" style={{ fontSize: 12.5 }}>Uncovered</Text>
            <Button size="small" icon={<StarOutlined />} onClick={onGenerate}>
              Generate
            </Button>
          </div>
        ) : (
          <>
            <Text style={{ fontSize: 12.5 }}>{result.headline}</Text>
            <Meter row={row} isDark={isDark} />
            {result.detail && <Text type="secondary" style={{ fontSize: 11.5 }}>{result.detail}</Text>}
          </>
        )}
      </div>
    </div>
  );
}

/** One bar per category, split by state, each segment its share of the category's scenarios. Never a single percentage. */
function Meter({ row, isDark }: { row: CoverageRow; isDark: boolean }) {
  const part = (n: number) => `${(100 * n) / row.scenarios}%`;
  return (
    <div
      aria-hidden
      style={{ display: 'flex', height: 6, borderRadius: 999, overflow: 'hidden', background: isDark ? colors.slate[700] : colors.slate[100] }}
    >
      <i style={{ width: part(row.passed), background: colors.green[500] }} />
      <i style={{ width: part(row.needsAttention), background: colors.red[500] }} />
      <i style={{ width: part(row.awaitingReview ?? 0), background: colors.ink[500] }} />
      <i style={{ width: part(row.evidenceIncomplete), background: colors.amber[500] }} />
      <i style={{ width: part(row.notAssessed), background: isDark ? colors.slate[500] : colors.slate[300] }} />
    </div>
  );
}
